import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  HostListener
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  base64ToFile,
  ImageCroppedEvent,
  ImageCropperModule
} from 'ngx-image-cropper';
import {
  Application,
  BucketFolders
} from '@enums/application.enums';
import { Messages } from '@enums/messages.enums';
import { CommonService } from '@shared/services/common.service';
import * as fromUserProfile from '@shared/user-profile.reducer';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { NEW_POST_CONST } from '@modules/dashboard/components/new-post/newposts/constants';

@Component({
  selector: 'app-image-cropper',
  templateUrl: './image-cropper.component.html',
  styleUrls: ['./image-cropper.component.scss'],
  standalone: true,
  imports: [CommonModule, ImageCropperModule]
})
export class ImageCropperComponent implements OnInit {
  private readonly MINIMUM_CONTAINER_WIDTH = 300;
  private readonly MINIMUM_DYNAMIC_WIDTH = 360;
  private readonly VIEWPORT_WIDTH_RATIO = 0.9;
  private readonly BASE_WIDTH_SMALL = 460;
  private readonly BASE_WIDTH_LARGE = 600;
  private readonly MAX_WIDTH_VW = '90vw';
  @Input() imageChangeEvent;
  @Input() imageWidth = 0;
  @Input() imageHeight = 0;
  @Input() emitProcessImage = true;
  @Input() cropperOptions = {
    cropperMinWidth: 450,
    cropperMinHeight: 250,
    cropperMaxWidth: null,
    cropperMaxHeight: null,
    cropperStaticWidth: null,
    cropperStaticHeight: null,
    recommendedWidth: null,
    recommendedHeight: null,
    recommendedText: null,
    aspectRatio: null,
    maintainAspectRatio: null,
    roundCropper: false
  };
  croppedImage: any = '';
  profile: File | ArrayBuffer;
  profilePreview: ArrayBuffer | File | string;
  @Input() isOpen;
  @Input() roundCropper = false;
  @Output() closeCrop = new EventEmitter();
  @Output() cropImageUpload = new EventEmitter();
  userProfileData;
  destroy$ = new Subject<void>();
  NEW_POST_CONSTANTS = NEW_POST_CONST;
  // Added properties for dynamic sizing
  containerWidth: number;
  zoomLevel: number = 1;
  @Input() spinner = false;

  constructor(
    public readonly _cs: CommonService,
    private readonly store: Store<fromUserProfile.UserProfileState>
  ) {}

  ngOnInit() {
    if (this._cs.isBrowser) {
      this.getProfile();
      if (!this.roundCropper) {
        this.roundCropper = this.cropperOptions.roundCropper;
      }
      this.calculateContainerWidth();
      this.detectZoomLevel();
    }
  }

  // Detect browser zoom level
  @HostListener('window:resize')
  onResize() {
    this.calculateContainerWidth();
    this.detectZoomLevel();
  }

  detectZoomLevel() {
    if (this._cs.isBrowser) {
      this.zoomLevel = window.devicePixelRatio || 1;
    }
  }

  calculateContainerWidth() {
    if (this._cs.isBrowser) {
      const viewportWidth = window.innerWidth;
      const maxAllowedWidth = viewportWidth * this.VIEWPORT_WIDTH_RATIO;
      const minAllowedWidth = Math.min(this.MINIMUM_DYNAMIC_WIDTH, maxAllowedWidth);
      const baseWidth =
        this.imageWidth <= this.cropperOptions.cropperMinWidth ? this.BASE_WIDTH_SMALL : this.BASE_WIDTH_LARGE;

      // Adjust width based on viewport and zoom
      if (
        this.imageWidth > this.cropperOptions.cropperMinWidth &&
        this.imageWidth < this.BASE_WIDTH_LARGE
      ) {
        this.containerWidth = Math.max(
          minAllowedWidth,
          Math.min(this.imageWidth, maxAllowedWidth)
        );
      } else {
        this.containerWidth = Math.max(
          minAllowedWidth,
          Math.min(baseWidth, maxAllowedWidth)
        );
      }
    }
  }

  getContainerClass() {
    return this.imageWidth <= this.cropperOptions.cropperMinWidth
      ? 'base-width'
      : 'responsive-width';
  }

  getContainerStyle() {
    const maxViewportPx = this._cs.isBrowser ? window.innerWidth * this.VIEWPORT_WIDTH_RATIO : this.MINIMUM_CONTAINER_WIDTH;
    const minWidth = this._cs.isBrowser
      ? `${Math.min(this.MINIMUM_CONTAINER_WIDTH, maxViewportPx)}px`
      : `${this.MINIMUM_CONTAINER_WIDTH}px`;

    if (
      this.imageWidth > this.cropperOptions.cropperMinWidth &&
      this.imageWidth < this.BASE_WIDTH_LARGE
    ) {
      return {
        width: this.containerWidth ? `${this.containerWidth}px` : null,
        minWidth,
        maxWidth: this.MAX_WIDTH_VW
      };
    }
    return {
      minWidth,
      maxWidth: this.MAX_WIDTH_VW
    };
  }

  imageLoaded() {
    // show cropper
  }

  cropperReady() {
    // cropper ready
  }

  cancelCrop(value) {
    this.closeCrop.emit(value);
  }

  pickProfile() {
    if (this.emitProcessImage) {
      this.cropImageUpload.emit(this.croppedImage);
      return;
    }
    const uploadFile = this.imageChangeEvent.target.files[Application.ZERO];
    const blob = base64ToFile(this.croppedImage);
    this.profile = new File([blob], uploadFile.name, { type: uploadFile.type });
    const isValid = this._cs.isValidFile(this.profile);
    if (isValid) {
      const fileNameValid = `${Application.BANNER + Date.now()}_${this.profile.name.toLowerCase().split(' ').join('_')}`;
      const obj = {
        file: this.profile,
        fileName: fileNameValid,
        profileData: this.croppedImage,
        folder: BucketFolders.coursesFolder
      };
      this.cropImageUpload.emit(obj);
    } else {
      this._cs.showErrorMessage(Messages.INVALID_FILE_TYPE);
    }
  }

  imageCropped(event: ImageCroppedEvent) {
    if (event && event.base64) {
      this.croppedImage = event.base64;
    }
  }

  getProfile() {
    this.store
      .select(fromUserProfile.getUserProfileItems)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.userProfileData = res;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
